import { apiClient } from './apiClient';
import axios from 'axios';
import { PUBLIC_BASE_URL } from '@/config/environment';

export interface SystemMetrics {
  cpu_usage_percent: number;
  memory_usage_percent: number;
  memory_available_gb: number;
  disk_usage_percent: number;
  disk_free_gb: number;
}

export interface PerformanceOverview {
  cache: {
    hit_rate: number;
    total_requests: number;
    cache_hits: number;
    cache_misses: number;
  };
  rate_limiting: {
    total_requests: number;
    rate_limited: number;
    rate_limit_percentage: number;
  };
  background_tasks: {
    total_tasks: number;
    running_tasks: number;
    completed_tasks: number;
    failed_tasks: number;
    pending_tasks: number;
  };
  compression: {
    total_responses: number;
    compressed_responses: number;
    compression_rate: number;
    bytes_saved: number;
  };
  api_performance: {
    total_requests: number;
    average_response_time: number;
    slow_requests: number;
    error_rate: number;
    uptime: number;
    memory_usage: number;
    cpu_usage: number;
  };
  summary: {
    cache_hit_rate: number;
    rate_limited_requests: number;
    active_background_tasks: number;
    compression_rate: number;
    average_response_time: number;
  };
}

export interface HealthStatus {
  status: string;
  timestamp: number;
  version: string;
  environment: string;
  database: {
    status: string;
    response_time: number;
  };
  redis: {
    status: string;
    enabled: boolean;
    url?: string;
  };
  system: SystemMetrics;
  performance: {
    cache_enabled: boolean;
    rate_limiting_enabled: boolean;
    compression_enabled: boolean;
  };
}

export interface SecurityStats {
  failed_logins: number;
  security_score: string;
  last_scan: string;
  active_sessions: number;
  blocked_ips: number;
  security_events: number;
}

export const systemService = {
  async getHealthStatus(): Promise<HealthStatus | null> {
    try {
      const response = await axios.get(`${PUBLIC_BASE_URL}/health/detailed`);
      return response.data;
    } catch (error) {
      console.warn('Health endpoint unavailable:', error);
      return null;
    }
  },

  async getPerformanceOverview(): Promise<PerformanceOverview | null> {
    try {
      return await apiClient.get('/performance/overview');
    } catch (error: any) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        console.warn('Performance overview requires authentication');
      } else if (error?.response?.status === 404) {
        console.warn('Performance overview endpoint not found');
      } else {
        console.warn('Performance overview endpoint error:', error?.message || error);
      }

      return null;
    }
  },

  async getPerformanceStats(): Promise<any> {
    try {
      return await apiClient.get('/performance/stats');
    } catch (error) {
      console.warn('Performance stats endpoint not available');
      return null;
    }
  },

  async getSystemMetrics(): Promise<SystemMetrics | null> {
    const health = await this.getHealthStatus();
    return health?.system ?? null;
  },

  async getSecurityStats(): Promise<SecurityStats | null> {
    try {
      return await apiClient.get('/performance/security');
    } catch (error) {
      console.warn('Security stats endpoint not available');
      return null;
    }
  },
};
